import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const STORAGE_KEY = 'assumptions_data';

const initialState = {
  roe: { inputs: { low: "", mid: "" }, historical: {} },
  revGrowth: { inputs: { low: "", mid: "" }, historical: {} },
  profitMargin: { inputs: { low: "", mid: "" }, historical: {} },
  fcfMargin: { inputs: { low: "", mid: "" }, historical: {} },
  pe: { inputs: { low: "", mid: "" }, historical: {} },
  pfcf: { inputs: { low: "", mid: "" }, historical: {} }
};

const AssumptionsContext = createContext(null);

export function AssumptionsProvider({ children }) {
  // Initialize state from localStorage or use initialState
  const [assumptions, setAssumptions] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : initialState;
    } catch (error) {
      console.error("Failed to parse assumptions_data:", error);
      return initialState;
    }
  });

  // Persist updates to localStorage whenever assumptions change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(assumptions));
    } catch (error) {
      console.error("Failed to save assumptions_data:", error);
    }
  }, [assumptions]);

  // Unified function to update assumptions
  const updateAssumption = useCallback((type, field, value, historical = {}) => {
    setAssumptions(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        inputs: {
          ...prev[type]?.inputs,
          [field]: value
        },
        historical: {
          ...prev[type]?.historical,
          ...historical
        }
      }
    }));
  }, []);

  const contextValue = useMemo(() => ({
    assumptions,
    updateAssumption,
  }), [assumptions, updateAssumption]);

  return (
    <AssumptionsContext.Provider value={contextValue}>
      {children}
    </AssumptionsContext.Provider>
  );
}

export function useAssumptions() {
  const context = useContext(AssumptionsContext);
  if (!context) {
    throw new Error('useAssumptions must be used within AssumptionsProvider');
  }
  return context;
}